import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useFieldReadability = () => {
  const [fieldReadability, setFieldReadability] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFieldReadability();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadFieldReadability();
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadFieldReadability = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // Load from localStorage if not authenticated
        const stored = localStorage.getItem("field_readability") === "true";
        applyFieldReadability(stored);
        setFieldReadability(stored);
        setLoading(false);
        return;
      }

      // Fetch from database
      const { data, error } = await supabase
        .from("user_preferences")
        .select("field_readability")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading field readability:", error);
        const stored = localStorage.getItem("field_readability") === "true";
        applyFieldReadability(stored);
        setFieldReadability(stored);
      } else if (data) {
        applyFieldReadability(data.field_readability);
        setFieldReadability(data.field_readability);
        localStorage.setItem("field_readability", data.field_readability.toString());
      } else {
        const stored = localStorage.getItem("field_readability") === "true";
        applyFieldReadability(stored);
        setFieldReadability(stored);
      }
    } catch (error) {
      console.error("Error in loadFieldReadability:", error);
      const stored = localStorage.getItem("field_readability") === "true";
      applyFieldReadability(stored);
      setFieldReadability(stored);
    } finally {
      setLoading(false);
    }
  };

  const applyFieldReadability = (enabled: boolean) => {
    console.log('Applying field readability:', enabled);
    console.log('Document element classes before:', document.documentElement.className);
    if (enabled) {
      document.documentElement.classList.add("field-readability");
    } else {
      document.documentElement.classList.remove("field-readability");
    }
    console.log('Document element classes after:', document.documentElement.className);
  };

  const toggleFieldReadability = async () => {
    const newValue = !fieldReadability;
    console.log('Toggling field readability from', fieldReadability, 'to', newValue);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { error } = await supabase
          .from("user_preferences")
          .upsert(
            {
              user_id: user.id,
              field_readability: newValue,
            },
            {
              onConflict: 'user_id'
            }
          );

        if (error) {
          console.error("Database error:", error);
          throw error;
        }
      }

      localStorage.setItem("field_readability", newValue.toString());
      applyFieldReadability(newValue);
      setFieldReadability(newValue);
      toast.success(`Field Readability Mode ${newValue ? "enabled" : "disabled"}`);
    } catch (error: any) {
      console.error("Error toggling field readability:", error);
      toast.error(error?.message || "Failed to update field readability setting");
    }
  };

  // Optional: Check ambient light sensor (if supported)
  const checkAmbientLight = async () => {
    try {
      if ('AmbientLightSensor' in window) {
        const sensor = new (window as any).AmbientLightSensor();
        sensor.addEventListener('reading', () => {
          if (sensor.illuminance > 800 && !fieldReadability) {
            toast("High ambient light detected. Enable Field Readability Mode?", {
              action: {
                label: "Enable",
                onClick: toggleFieldReadability,
              },
            });
          }
        });
        sensor.start();
      }
    } catch (error) {
      console.log("Ambient light sensor not available:", error);
    }
  };

  useEffect(() => {
    if (!loading) {
      checkAmbientLight();
    }
  }, [loading]);

  return { fieldReadability, loading, toggleFieldReadability };
};
